"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import type { ComponentType } from "react";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { resolveWaveParticipationVariant, type WaveParticipationVariant } from "@/helpers/waves/wave-participation-presentation.helpers";
import { MemesLeaderboardDrop } from "@/components/memes/drops/MemesLeaderboardDrop";
import { DefaultWaveSmallLeaderboardDrop } from "@/components/waves/small-leaderboard/DefaultWaveSmallLeaderboardDrop";
import { MemesWaveSmallLeaderboardDrop } from "@/components/waves/small-leaderboard/MemesWaveSmallLeaderboardDrop";
import { QuorumWaveSmallLeaderboardDrop } from "@/components/waves/small-leaderboard/QuorumWaveSmallLeaderboardDrop";
import { DefaultWaveLeaderboardDrop } from "./drops/DefaultWaveLeaderboardDrop";
import { QuorumWaveLeaderboardDrop } from "./drops/QuorumWaveLeaderboardDrop";
interface WaveLeaderboardDropRendererProps {
  readonly drop: ExtendedDrop;
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface WaveSmallLeaderboardDropRendererProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: () => void;
}
interface WaveLeaderboardRendererSet {
  readonly LeaderboardDrop: ComponentType<WaveLeaderboardDropRendererProps>;
  readonly SmallLeaderboardDrop: ComponentType<WaveSmallLeaderboardDropRendererProps>;
}
interface ResolvedWaveLeaderboardRendererSet extends WaveLeaderboardRendererSet {
  readonly variant: WaveParticipationVariant;
}
const WAVE_LEADERBOARD_VARIANT_OVERRIDES: Readonly<Partial<Record<string, WaveParticipationVariant>>> = {};
const DefaultLeaderboardDropRenderer: React.FC<WaveLeaderboardDropRendererProps> = t0 => {
  const $ = _c(3);
  const {
    drop,
    onDropClick
  } = t0;
  let t1;
  if ($[0] !== drop || $[1] !== onDropClick) {
    t1 = <DefaultWaveLeaderboardDrop drop={drop} onDropClick={onDropClick} />;
    $[0] = drop;
    $[1] = onDropClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const QuorumLeaderboardDropRenderer: React.FC<WaveLeaderboardDropRendererProps> = t0 => {
  const $ = _c(3);
  const {
    drop,
    onDropClick
  } = t0;
  let t1;
  if ($[0] !== drop || $[1] !== onDropClick) {
    t1 = <QuorumWaveLeaderboardDrop drop={drop} onDropClick={onDropClick} />;
    $[0] = drop;
    $[1] = onDropClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const MemesLeaderboardDropRenderer: React.FC<WaveLeaderboardDropRendererProps> = t0 => {
  const $ = _c(4);
  const {
    drop,
    wave,
    onDropClick
  } = t0;
  let t1;
  if ($[0] !== drop || $[1] !== onDropClick || $[2] !== wave) {
    t1 = <MemesLeaderboardDrop drop={drop} wave={wave} onDropClick={onDropClick} />;
    $[0] = drop;
    $[1] = onDropClick;
    $[2] = wave;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const WAVE_LEADERBOARD_RENDERERS: Readonly<Record<WaveParticipationVariant, WaveLeaderboardRendererSet>> = {
  default: {
    LeaderboardDrop: DefaultLeaderboardDropRenderer,
    SmallLeaderboardDrop: DefaultWaveSmallLeaderboardDrop
  },
  memes: {
    LeaderboardDrop: MemesLeaderboardDropRenderer,
    SmallLeaderboardDrop: MemesWaveSmallLeaderboardDrop
  },
  curation: {
    LeaderboardDrop: DefaultLeaderboardDropRenderer,
    SmallLeaderboardDrop: DefaultWaveSmallLeaderboardDrop
  },
  quorum: {
    LeaderboardDrop: QuorumLeaderboardDropRenderer,
    SmallLeaderboardDrop: QuorumWaveSmallLeaderboardDrop
  }
};
export const useWaveLeaderboardRendererSet = waveId => {
  const $ = _c(8);
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useSeizeSettings();
  let t0;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== waveId) {
    t0 = resolveWaveParticipationVariant({
      waveId,
      overrides: WAVE_LEADERBOARD_VARIANT_OVERRIDES,
      isMemesWave,
      isCurationWave,
      isQuorumWave
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = waveId;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const variant = t0;
  const t1 = WAVE_LEADERBOARD_RENDERERS[variant];
  let t2;
  if ($[5] !== t1 || $[6] !== variant) {
    t2 = {
      variant,
      ...t1
    };
    $[5] = t1;
    $[6] = variant;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};